/**
 * Auth Callback Page
 *
 * Handles Supabase auth redirects (email confirmations, password resets, invitations)
 */

import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import { Spinner } from '../components/ui'
import api from '../services/api'

export function AuthCallback() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { checkAuth } = useAuthStore()
  const [status, setStatus] = useState('processing')
  const [error, setError] = useState(null)

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Get the invitation token if present
        const invitationToken = searchParams.get('invitation')

        // Check for Supabase hash params (access_token, etc.)
        const hash = window.location.hash
        if (hash) {
          // Parse the hash params
          const params = new URLSearchParams(hash.substring(1))
          const accessToken = params.get('access_token')
          const refreshToken = params.get('refresh_token')
          const type = params.get('type')

          if (accessToken) {
            // Store the token
            api.setToken(accessToken)

            // If this is an invite, mark the invitation as accepted and assign role
            if (invitationToken) {
              try {
                await api.request('/auth/accept-invite', { token: invitationToken }, 'POST')
              } catch (err) {
                console.error('Failed to accept invite:', err)
                // Continue anyway - user is authenticated
              }
            }

            // Refresh auth state
            await checkAuth()

            setStatus('success')
            setTimeout(() => {
              navigate('/admin', { replace: true })
            }, 1500)
            return
          }
        }

        // No hash params - might be a direct visit or error
        // Check if user is already authenticated
        await checkAuth()
        const { isAuthenticated, user } = useAuthStore.getState()

        if (isAuthenticated) {
          navigate(user?.isAdmin ? '/admin' : '/dashboard', { replace: true })
        } else {
          navigate('/login', { replace: true })
        }
      } catch (err) {
        console.error('Auth callback error:', err)
        setError(err.message)
        setStatus('error')
      }
    }

    handleCallback()
  }, [navigate, searchParams, checkAuth])

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-[#0f0f0f] flex items-center justify-center p-4">
        <div className="bg-[#1a1a1a] rounded-[6px] p-8 max-w-md w-full text-center">
          <div className="h-16 w-16 rounded-full bg-[#fde8e8]/20 flex items-center justify-center mx-auto mb-4">
            <svg className="h-8 w-8 text-[#e05252]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h1 className="text-xl font-semibold text-white mb-2">Something went wrong</h1>
          <p className="text-[#b0b0b0] mb-6">{error || 'Failed to complete authentication'}</p>
          <button
            onClick={() => navigate('/login')}
            className="px-4 py-2 bg-[#8a63d2] text-white rounded-[6px] hover:bg-[#6d47b8] transition-colors"
          >
            Go to Login
          </button>
        </div>
      </div>
    )
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen bg-[#0f0f0f] flex items-center justify-center p-4">
        <div className="bg-[#1a1a1a] rounded-[6px] p-8 max-w-md w-full text-center">
          <div className="h-16 w-16 rounded-full bg-[#e8f5e9]/20 flex items-center justify-center mx-auto mb-4">
            <svg className="h-8 w-8 text-[#3db843]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-xl font-semibold text-white mb-2">Welcome!</h1>
          <p className="text-[#b0b0b0]">Redirecting to dashboard...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-[#0f0f0f] flex items-center justify-center p-4">
      <div className="bg-[#1a1a1a] rounded-[6px] p-8 max-w-md w-full text-center">
        <Spinner className="mx-auto mb-4" />
        <h1 className="text-xl font-semibold text-white mb-2">Setting up your account...</h1>
        <p className="text-[#b0b0b0]">Please wait a moment</p>
      </div>
    </div>
  )
}

export default AuthCallback
